import type { ReactNode } from 'react';
import { Badge } from '@/shared/ui/badge';
import type { DetailAdjacent, DetailStatus } from './types';
import { styles } from './styles';
import { AccentHeader } from './accent-header';
import { PrevNextNav } from './nav-buttons';

/**
 * The detail-page header.
 *
 * Two variants. `neutral` is the default: a plain row with the title,
 * optional subtitle, status badge and right-aligned action area. `accent` is
 * the commerce-style elevated card with prev/next nav, primary + secondary
 * action buttons, and (optionally) the tab strip inside the card footer.
 *
 * Breadcrumbs are rendered ABOVE the accent card by the orchestrator, not
 * inside this component, so the card stays focused on the title.
 *
 * The variant is selected by the parent via `headerVariant`. Everything
 * inside is optional — `title` is the only required field — so the same
 * component serves both the "I just need a title" case and the "give me the
 * full page frame" case.
 */
export function DetailHeader(props: DetailHeaderProps): ReactNode {
  if (props.headerVariant === 'accent') {
    return <AccentVariant {...props} />;
  }
  return <NeutralVariant {...props} />;
}

/** The plain header: a title row with optional subtitle, status badge and actions. */
function NeutralVariant({ title, subtitle, status, backTo, actions }: DetailHeaderProps): ReactNode {
  return (
    <header className={styles.header}>
      <div className={styles.headerText}>
        {backTo ? (
          <a href={backTo} className={styles.backLink}>
            Back
          </a>
        ) : null}
        <h1 className={styles.title}>{title}</h1>
        {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
      </div>

      <div className={styles.headerAside}>
        {status ? <Badge tone={status.tone}>{status.label}</Badge> : null}
        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </div>
    </header>
  );
}

/** The commerce-style header. Delegates the heavy lifting to `AccentHeader`. */
function AccentVariant(props: DetailHeaderProps): ReactNode {
  return (
    <AccentHeader
      title={props.title}
      subtitle={props.subtitle}
      status={props.status}
      badges={props.badges}
      prev={props.prev}
      next={props.next}
      primaryAction={props.primaryAction}
      secondaryAction={props.secondaryAction}
      tabs={props.tabs}
      activeTabId={props.activeTabId}
      onTabChange={props.onTabChange}
      tabsInHeader={props.tabsInHeader}
      PrevNextSlot={PrevNextNav}
    />
  );
}

type DetailHeaderProps = {
  title: string;
  subtitle?: string;
  status?: DetailStatus;
  backTo?: string;
  badges?: ReadonlyArray<ReactNode>;
  prev?: DetailAdjacent;
  next?: DetailAdjacent;
  primaryAction?: ReactNode;
  secondaryAction?: ReactNode;
  actions?: ReactNode;
  tabs?: ReadonlyArray<{
    readonly id: string;
    readonly label: string;
    readonly icon?: ReactNode;
    readonly count?: number;
  }>;
  activeTabId?: string;
  onTabChange?: (id: string) => void;
  tabsInHeader?: boolean;
  headerVariant?: 'neutral' | 'accent';
};